import { LUXURY_ESCAPES } from '~/consts/brands';

import UsersService from '~/services/UsersService';
import { chunkArray } from '~/utils/chunkArray';

export type EligibilityContact = {
  id: string;
  email: string;
  phonePrefix: string | null;
  phone: string | null;
};

export type ExistingMemberAccount = {
  customerId: string;
  email?: string;
  matchedBy: 'email' | 'phone';
};

export type ExistingMemberMatch = {
  accounts: Array<ExistingMemberAccount>;
  multiplePhoneMatches: boolean;
};

type UserSearchSummary = {
  email?: string;
  id_member?: string | number;
  memberId?: string | number;
  id?: string | number;
};

const EMPTY_MATCH: ExistingMemberMatch = { accounts: [], multiplePhoneMatches: false };
const USER_SEARCH_CONCURRENCY = 8;

export type LookupExistingMemberAccountsOptions = {
  concurrency?: number;
  priorityIds?: ReadonlySet<string>;
  onBatch?: (partial: Record<string, ExistingMemberMatch>) => void;
};

function normaliseEligibilityEmail(email: string): string {
  return email.trim().toLowerCase();
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

function pickCustomerId(record: Record<string, unknown>): string | undefined {
  const value = record.id_member ?? record.memberId ?? record.id_account ?? record.id;
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
}

export function eligibilityPhoneLookup(
  eligibility: Pick<EligibilityContact, 'phonePrefix' | 'phone'>,
): { phonePrefix: string; phone: string } | undefined {
  const phonePrefix = (eligibility.phonePrefix ?? '').replace(/[^0-9]/g, '');
  const phone = String(eligibility.phone ?? '')
    .replace(/[^0-9]/g, '')
    .replace(/^0/, '');
  if (!phonePrefix || !phone) {
    return undefined;
  }
  return { phonePrefix, phone };
}

export function formatExistingMemberAccounts(match: ExistingMemberMatch | undefined): string {
  if (match === undefined || !Array.isArray(match.accounts)) {
    return '';
  }

  const labels = match.accounts.map((account) => account.customerId);
  if (match.multiplePhoneMatches) {
    labels.push('Multiple phone matches');
  }
  return labels.join(', ');
}

function asUserSearchSummaries(response: unknown): Array<UserSearchSummary> {
  if (Array.isArray(response)) {
    return response as Array<UserSearchSummary>;
  }
  if (!response || typeof response !== 'object') {
    return [];
  }

  const record = response as { result?: unknown; users?: unknown; data?: unknown };
  for (const candidate of [record.result, record.users, record.data]) {
    if (Array.isArray(candidate)) {
      return candidate as Array<UserSearchSummary>;
    }
    if (candidate && typeof candidate === 'object') {
      const nested = candidate as { result?: unknown; users?: unknown; data?: unknown };
      for (const nestedCandidate of [nested.result, nested.users, nested.data]) {
        if (Array.isArray(nestedCandidate)) {
          return nestedCandidate as Array<UserSearchSummary>;
        }
      }
    }
  }

  return [];
}

function asUserRecord(user: unknown): Record<string, unknown> | undefined {
  if (!user || typeof user !== 'object') {
    return undefined;
  }
  const record = user as Record<string, unknown>;
  if (pickCustomerId(record) || typeof record.email === 'string') {
    return record;
  }
  if (record.result && typeof record.result === 'object') {
    return record.result as Record<string, unknown>;
  }
  return record;
}

function toExistingMemberAccount(
  user: unknown,
  matchedBy: ExistingMemberAccount['matchedBy'],
): ExistingMemberAccount | undefined {
  const record = asUserRecord(user);
  if (!record) {
    return undefined;
  }
  const customerId = pickCustomerId(record);
  if (!customerId) {
    return undefined;
  }
  const email = typeof record.email === 'string' && record.email ? record.email : undefined;
  return { customerId, email, matchedBy };
}

function mergeExistingMemberAccounts(
  emailAccounts: Array<ExistingMemberAccount>,
  phoneMatch: ExistingMemberMatch,
): ExistingMemberMatch {
  const accountsById = new Map<string, ExistingMemberAccount>();
  for (const account of [...emailAccounts, ...phoneMatch.accounts]) {
    if (!accountsById.has(account.customerId)) {
      accountsById.set(account.customerId, account);
    }
  }
  return {
    accounts: [...accountsById.values()],
    multiplePhoneMatches: phoneMatch.multiplePhoneMatches,
  };
}

async function lookupAccountsByEmail(email: string, signal?: AbortSignal): Promise<Array<ExistingMemberAccount>> {
  try {
    const users = asUserSearchSummaries(
      await UsersService.getUsers(
        {
          filter: email,
          limit: 10,
          offset: 0,
          brand: LUXURY_ESCAPES,
        },
        { signal },
      ),
    );
    const normalisedEmail = normaliseEligibilityEmail(email);
    return users.flatMap((user) => {
      if (user.email?.toLowerCase() !== normalisedEmail) {
        return [];
      }
      const account = toExistingMemberAccount(user, 'email');
      return account ? [account] : [];
    });
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    return [];
  }
}

async function lookupAccountsByPhone(
  phonePrefix: string,
  phone: string,
  signal?: AbortSignal,
): Promise<ExistingMemberMatch> {
  try {
    const result = await UsersService.getUserByPhone(phonePrefix, phone, {
      brand: LUXURY_ESCAPES,
      signal,
    });
    if (result.kind === 'none') {
      return EMPTY_MATCH;
    }
    if (result.kind === 'multiple') {
      return { accounts: [], multiplePhoneMatches: true };
    }
    const account = toExistingMemberAccount(result.user, 'phone');
    return { accounts: account ? [account] : [], multiplePhoneMatches: false };
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    return EMPTY_MATCH;
  }
}

function matchForEligibility(
  eligibility: EligibilityContact,
  emailAccounts: Map<string, Array<ExistingMemberAccount>>,
  phoneMatches: Map<string, ExistingMemberMatch>,
): ExistingMemberMatch {
  const email = normaliseEligibilityEmail(eligibility.email);
  const phone = eligibilityPhoneLookup(eligibility);
  const phoneKey = phone ? `${phone.phonePrefix}:${phone.phone}` : undefined;
  return mergeExistingMemberAccounts(
    emailAccounts.get(email) ?? [],
    phoneKey ? (phoneMatches.get(phoneKey) ?? EMPTY_MATCH) : EMPTY_MATCH,
  );
}

export async function lookupExistingMemberAccounts(
  eligibilities: Array<EligibilityContact>,
  signal?: AbortSignal,
  options?: LookupExistingMemberAccountsOptions,
): Promise<Record<string, ExistingMemberMatch>> {
  const concurrency = Math.max(1, options?.concurrency ?? USER_SEARCH_CONCURRENCY);
  const orderedEligibilities =
    options?.priorityIds && options.priorityIds.size > 0
      ? [
          ...eligibilities.filter((eligibility) => options.priorityIds?.has(eligibility.id)),
          ...eligibilities.filter((eligibility) => !options.priorityIds?.has(eligibility.id)),
        ]
      : eligibilities;

  const emailAccounts = new Map<string, Array<ExistingMemberAccount>>();
  const phoneMatches = new Map<string, ExistingMemberMatch>();
  const results: Record<string, ExistingMemberMatch> = {};

  for (const batch of chunkArray(orderedEligibilities, concurrency)) {
    const emailsToSearch = new Map<string, string>();
    const phonesToSearch = new Map<string, { phonePrefix: string; phone: string }>();
    for (const eligibility of batch) {
      const normalisedEmail = normaliseEligibilityEmail(eligibility.email);
      if (normalisedEmail && !emailAccounts.has(normalisedEmail) && !emailsToSearch.has(normalisedEmail)) {
        emailsToSearch.set(normalisedEmail, eligibility.email.trim());
      }
      const phone = eligibilityPhoneLookup(eligibility);
      if (phone) {
        const phoneKey = `${phone.phonePrefix}:${phone.phone}`;
        if (!phoneMatches.has(phoneKey) && !phonesToSearch.has(phoneKey)) {
          phonesToSearch.set(phoneKey, phone);
        }
      }
    }

    const [emailResults, phoneResults] = await Promise.all([
      Promise.all(
        [...emailsToSearch.entries()].map(
          async ([normalisedEmail, email]): Promise<[string, Array<ExistingMemberAccount>]> => [
            normalisedEmail,
            await lookupAccountsByEmail(email, signal),
          ],
        ),
      ),
      Promise.all(
        [...phonesToSearch.entries()].map(
          async ([key, phone]): Promise<[string, ExistingMemberMatch]> => [
            key,
            await lookupAccountsByPhone(phone.phonePrefix, phone.phone, signal),
          ],
        ),
      ),
    ]);
    for (const [normalisedEmail, accounts] of emailResults) {
      emailAccounts.set(normalisedEmail, accounts);
    }
    for (const [key, match] of phoneResults) {
      phoneMatches.set(key, match);
    }

    const partial: Record<string, ExistingMemberMatch> = {};
    for (const eligibility of batch) {
      const match = matchForEligibility(eligibility, emailAccounts, phoneMatches);
      results[eligibility.id] = match;
      partial[eligibility.id] = match;
    }
    options?.onBatch?.(partial);
  }

  return results;
}
